import { Link, useParams } from 'react-router-dom'
import { useModalityById } from '@/hooks/use-modality-by-id'
import { Pencil, ChevronLeft } from 'lucide-react'
import { useEffect } from 'react'
import { PageContainer } from '@/component/layout'

export const ModalityDetail = () => {
  const { id } = useParams<{ id: string }>()
  const { item, loading, error } = useModalityById(id)

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!id) return <div className='p-6'>Missing id</div>
    if (loading) return <PageContainer className='text-center'>Loading modality…</PageContainer>
  if (error) return <div className='p-6 text-red-600'>Error: {error}</div>
  if (!item) return <div className='p-6'>Not found</div>

  const title = item.name ?? `Modality #${id}`
  const description = item.description ?? '—'
  const distribution = item.distribution ?? item.keyFeatures ?? '—'

  return (
    <PageContainer>
      {/* Back link */}
      <div className='mb-6'>
        <Link to='/modalities' className='inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900'>
          <span className='-ml-1'>
            <ChevronLeft />
          </span>{' '}
          Back to Modality Manager
        </Link>
      </div>

      {/* Header */}
      <div className='mb-4 flex items-start justify-between'>
        <h1 className='text-3xl font-bold'>{title}</h1>

        <Link
          to={`/modalities/${id}/edit`}
          className='inline-flex items-center gap-2 bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800'
          title='Edit modality'>
          Edit <Pencil className='w-4 h-4' />
        </Link>
      </div>

      {/* Sections */}
      <DetailSection title='Modality Description' tooltip='A summary of how this modality works in learning contexts.' content={description} />

      <DetailSection title='Modality Distribution' tooltip='Additional context or dissemination notes for this modality.' content={distribution} />

      {/* Optional: change notes if present */}
      {item.changeDescription ? (
        <DetailSection title='Change Notes' tooltip='Description of the last change made to this modality.' content={item.changeDescription} />
      ) : null}
    </PageContainer>
  )
}

/* ---------- UI helpers ---------- */

function DetailSection({ title, tooltip, content }: { title: string; tooltip?: string; content: string }) {
  return (
    <div className='mb-6 rounded-xl border border-gray-200 bg-white p-5'>
      <div className='mb-2 flex items-center gap-2'>
        <div className='text-sm font-semibold text-gray-900'>{title}</div>
        <span title={tooltip}>{/* <Info className='h-4 w-4 text-gray-400' /> */}</span>
      </div>
      <p className='text-gray-800 leading-relaxed whitespace-pre-wrap'>
        {content && String(content).trim().length ? content : <span className='text-gray-400'>—</span>}
      </p>
    </div>
  )
}

// function MetaPill({ label, value, tone = 'gray' }: { label: string; value: string; tone?: 'gray' | 'green' }) {
//   const styles = tone === 'green' ? 'bg-green-50 text-green-700 ring-green-200' : 'bg-gray-50 text-gray-700 ring-gray-200'
//   return (
//     <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 ring-1 ${styles}`}>
//       <span className='font-medium'>{label}:</span>
//       <span>{value}</span>
//     </span>
//   )
// }
